import Toast from "@ui-kit/feedback/alerts/Toast/Toast.web";
import { INotifier } from "./INotifier";

/**
 * WebToastNotifier is responsible for displaying toast notifications within the web application.
 * It encapsulates the logic to determine whether the application is running in a legacy dashboard context
 * and adjusts the notification display mechanism accordingly. In a legacy dashboard environment, notifications
 * are dispatched as events to the parent window to be handled by the legacy system. Otherwise, it utilizes
 * a standard toast component for notification display.
 *
 *
 * Usage:
 *   const notifier = NotifierFactory.createNotifier();
 *   notifier.error("Error message");
 *   notifier.success("Success message");
 *   notifier.info("Info message");
 */
export class WebToastNotifier implements INotifier {
  private toastHandler = Toast;

  /**
   * Generate a consistent ID from a message for Chakra's deduplication
   */
  private static generateMessageId(message: string, type?: string): string {
    const content = `${type || ""}_${String(message || "")
      .trim()
      .toLowerCase()}`;

    // Simple hash function for generating consistent IDs
    let hash = 0;
    for (let i = 0; i < content.length; i++) {
      const char = content.charCodeAt(i);
      // eslint-disable-next-line no-bitwise
      hash = (hash << 5) - hash + char;
      // eslint-disable-next-line no-bitwise
      hash |= 0; // Convert to 32-bit integer
    }
    return `toast_${Math.abs(hash)}`;
  }

  public error(message: string): void {
    this.displayToast(message, "error");
  }

  public success(message: string): void {
    this.displayToast(message, "success");
  }

  public info(message: string): void {
    this.displayToast(message, "info");
  }

  /**
   * Displays a toast notification using Chakra's built-in deduplication.
   * If in a legacy dashboard context, it dispatches an event to the parent window.
   * Otherwise, it uses the standard toast component for notification.
   *
   * @param message - The message to be displayed in the toast notification.
   * @param type - The type of the toast notification (error, success, info).
   */
  private displayToast(
    message: string,
    type: "error" | "success" | "warning" | "info" // TODO: Add alert type
  ): void {
    // Generate a consistent ID for this message
    const messageId = WebToastNotifier.generateMessageId(message, type);

    if (WebToastNotifier.isLegacyDashboard()) {
      // Dispatches a custom event to the parent window for legacy dashboard notifications
      const event = new CustomEvent("displayLegacyToast", {
        detail: { status: type, message },
      });
      window.parent.document.dispatchEvent(event);
    } else {
      // Use Chakra's built-in deduplication by passing the ID
      // Chakra will check if a toast with this ID is already active
      const capitalizedTitle = type.charAt(0).toUpperCase() + type.slice(1);
      // Ensure message is a string (might receive Error objects or other types)
      const formattedMessage =
        typeof message === "string" ? message : String(message ?? "");

      this.toastHandler({
        status: type,
        title: capitalizedTitle,
        description: formattedMessage,
        id: messageId, // Chakra handles deduplication with this ID
      });
    }
  }

  /**
   * Determines if the current context is within a legacy dashboard based on the URL path.
   * It checks for the presence of '/dashboard/' in the path, which indicates a legacy dashboard.
   *
   * @returns True if the current context is a legacy dashboard, false otherwise.
   */
  private static isLegacyDashboard(): boolean {
    if (typeof window === "undefined") {
      return false;
    }
    // Regular expression to test if the URL path contains '/dashboard/' indicating a legacy dashboard
    const legacyDashboardRegex = /\/dashboard\//;
    const browserPath =
      window.location !== window.parent.location
        ? document.referrer
        : document.location.pathname;
    return legacyDashboardRegex.test(browserPath);
  }
}
